import React, { useEffect, useMemo, useRef, useState } from 'react'

// =========================================================================
// TRADUCTOR VISUAL (Para los textos de Ganador en Pantalla)
// =========================================================================
const getVisualZoneName = (realZoneName) => {
    if (!realZoneName) return '';
    const normalized = realZoneName.toLowerCase().trim();

    // Mapeamos los nombres internos a los nombres visuales
    const aliases = {
        'zona1': 'Xolo Mayor Rojo',
        'zona2': 'Xolo Mayor Blanco'
    };

    return aliases[normalized] || realZoneName;
};

const GoalScreenV2 = ({ currentMatchState }) => {
    const {
        phase = 'WAITING',
        elapsedMilliseconds = 0,
        totalDurationMilliseconds = 1,
        zonesProgress = [],
        winner = null
    } = currentMatchState || {};

    const [viewStage, setViewStage] = useState('RACING');
    const maxPositionsHistoryRef = useRef({});

    useEffect(() => {
        const currentPhase = phase.toUpperCase();

        if (currentPhase === 'WAITING' || currentPhase === 'IDLE') {
            maxPositionsHistoryRef.current = {};

        } else if (currentPhase === 'RUNNING') {
            setViewStage('RACING');

        } else if (currentPhase === 'FINISHED') {
            const isSingleWinner = winner && winner !== 'tie' && !winner.startsWith('tie_partial:');

            if (isSingleWinner) {
                setViewStage('KICK_ANIMATION');
                setTimeout(() => {
                    setViewStage('WINNER_TEXT');
                }, 2000);
            } else {
                setViewStage('WINNER_TEXT');
            }
        }
    }, [phase, winner]);

    // =========================================================================
    // AGRUPACIÓN INTELIGENTE: Consolida todas las zonas zombis en solo 2 carriles
    // =========================================================================
    const dynamicZonesToRender = useMemo(() => {
        if (!zonesProgress || zonesProgress.length === 0) return [];
        
        const grouped = {};
        
        zonesProgress.forEach(z => {
            const rawZone = (z.zoneIdentifier || z.zone || 'unknown').toLowerCase().trim();
            // AGRUPAMOS POR EL NOMBRE INTERNO (zona1, zona2) para no perder la referencia de la skin
            
            if (!grouped[rawZone]) {
                grouped[rawZone] = {
                    zoneIdentifier: rawZone, 
                    totalTaps: 0
                };
            }
            // Sumamos los taps de todos los que pertenezcan a este ID interno
            grouped[rawZone].totalTaps += (z.totalTaps || 0);
        });

        // Convertimos a array y ordenamos (zona1 arriba, zona2 abajo)
        return Object.values(grouped).sort((a, b) => a.zoneIdentifier.localeCompare(b.zoneIdentifier));
    }, [zonesProgress]);

    const timeCompletedPercentage = useMemo(() => {
        if (phase.toUpperCase() === 'WAITING' || phase.toUpperCase() === 'IDLE') return 0;
        const percentage = (elapsedMilliseconds / totalDurationMilliseconds) * 100;
        return Math.min(100, Math.max(0, percentage));
    }, [elapsedMilliseconds, totalDurationMilliseconds, phase]);

    // 1. Calculamos al VERDADERO líder de la carrera usando nuestras zonas ya agrupadas
    const highestTapsOverall = useMemo(() => {
        if (!dynamicZonesToRender || dynamicZonesToRender.length === 0) return 0;
        return Math.max(...dynamicZonesToRender.map(z => z.totalTaps || 0));
    }, [dynamicZonesToRender]);

    const MAXIMUM_VISUAL_LAG_PERCENTAGE = 25; // Máximo rezago visual (25%)

    // =========================================================================
    // ASIGNACIÓN DE IMÁGENES POR ZONA
    // =========================================================================
    // Redbull SoapBox
    const ZONES_SKINS = {
        'zona1': { avatar: '/img/games/runtogoal/carrera-atleti/lucho.png', spark: '/img/games/runtogoal/carrera-atleti/destellos-amarillos.png' },
        'zona2': { avatar: '/img/games/runtogoal/carrera-atleti/tuntita.png', spark: '/img/games/runtogoal/carrera-atleti/destellos-amarillos.png' },
        'default': { avatar: '/img/games/runtogoal/carrera-atleti/lucho.png', spark: '/img/games/runtogoal/carrera-atleti/destellos-amarillos.png' }
    };

    // =========================================================================
    // HELPER PARA DETERMINAR IMAGEN DEL GANADOR
    // =========================================================================
    const getWinnerImage = (winnerRaw) => {
        if (!winnerRaw) return "/img/games/runtogoal/carrera-atleti/lucho-meta.png"; // Fallback por defecto
        const normalized = winnerRaw.toLowerCase().trim();
        
        // Comparamos contra el ID interno
        if (normalized === 'zona2') {
            return "/img/games/runtogoal/carrera-atleti/tuntita-meta.png";
        }
        
        return "/img/games/runtogoal/carrera-atleti/lucho-meta.png";
    };

    const renderWinnerTextScreen = (winnerRaw) => {
        if (winnerRaw === 'tie') return '¡ES UN EMPATE!';
        if (winnerRaw.startsWith('tie_partial:')) {
            const tiedZones = winnerRaw.split(':')[1].split(' y ');
            const uniqueAliases = [...new Set(tiedZones.map(z => getVisualZoneName(z).toUpperCase()))];
            return `¡HAN EMPATADO ${uniqueAliases.join(' Y ')}!`;
        }

        return `¡HA GANADO ${getVisualZoneName(winnerRaw).toUpperCase()}!`;
    };


    return (
        <>
            <div className='GoalScreenX__Sponsor' style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '6px 0', marginTop: '12px' }}>
                <img src="/img/page/xolos-five-black.png" className='GoalScreenX__Sponsor__Image' style={{ width: '100%', maxWidth: '280px' }} alt="Sponsor" />
            </div>

            <div className="goal">
                <div className="goal__pitch Goal__V2__Background"></div>

                <div className="goal__lanes" style={{ gridTemplateRows: `repeat(${dynamicZonesToRender.length}, 1fr)` }}>
                    {dynamicZonesToRender.map((zoneItem, index) => {
                        const zoneId = zoneItem.zoneIdentifier || `unknown-${index}`;
                        
                        // 2. MATEMÁTICA CORREGIDA (Avance suave basado en el líder real)
                        let finalPosition = timeCompletedPercentage;

                        if (highestTapsOverall > 0) {
                            const tapsDeficit = highestTapsOverall - (zoneItem.totalTaps || 0);
                            const lagRatio = tapsDeficit / highestTapsOverall;
                            // El líder va con el reloj (0 deficit). El que va perdiendo se retrasa visualmente.
                            finalPosition -= (lagRatio * MAXIMUM_VISUAL_LAG_PERCENTAGE);
                        }

                        // 3. EVITAR QUE CAMINEN PARA ATRÁS
                        if (phase.toUpperCase() === 'RUNNING') {
                            const previousMaxPosition = maxPositionsHistoryRef.current[zoneId] || 0;
                            if (finalPosition < previousMaxPosition) {
                                finalPosition = previousMaxPosition;
                            } else {
                                maxPositionsHistoryRef.current[zoneId] = finalPosition;
                            }
                        } else if (phase.toUpperCase() === 'WAITING' || phase.toUpperCase() === 'IDLE') {
                            finalPosition = 0;
                        }

                        finalPosition = Math.min(100, Math.max(0, finalPosition));
                        const safeZoneId = (zoneId || '').trim().toLowerCase();
                        const skin = ZONES_SKINS[safeZoneId] || ZONES_SKINS["default"];

                        // 3. CORRECCIÓN DE PERSPECTIVA DE LA CANCHA
                        // Ajuste de Perspectiva: Mueve SOLO LA META.
                         // El carril superior frena a 20vw del borde. El inferior frena a 14vw (para igualar la portería inclinada).
                        // Al estar en 0%, ambos arrancan desde la misma línea exacta.
                        const finishGap = 20 - (index * 3); // Carrera

                        return (
                            <div key={zoneId} className="goal__lane" style={{ '--finish-gap': `${finishGap}vw` }}>
                                <div className="goal__runner" style={{ '--pct': finalPosition / 100 }}>
                                    <img className="goal__velocity" style={{ width: '150px' }} src={skin.spark} alt="destellos" />
                                    <img className="goal__avatar" src={skin.avatar} alt="Jugador" />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {viewStage === 'KICK_ANIMATION' && (
                    <div className="goal-outro">
                        <img src={getWinnerImage(winner)} className="goal-outro__ball" alt="Ganador" />
                    </div>
                )}

                {viewStage === 'WINNER_TEXT' && (
                    <div className="goal__pov">
                        {winner ? (
                            <div className="goal__povGoool text-center">
                                {renderWinnerTextScreen(winner)}
                            </div>
                        ) : (
                            <div className="goal__povGoool text-center">¡CARRERA FINALIZADA!</div>
                        )}
                        <div className="goal__fw">
                            <div className="goal__fwBurst" />
                            <div className="goal__fwBurst goal__fwBurst--b2" />
                            <div className="goal__fwBurst goal__fwBurst--b3" />
                        </div>
                    </div>
                )}
            </div>

            <footer className='goal__footer'>
                <p className='text-center FooterCredits'>Hecho con ♥ por Interaktia | 2026</p>
            </footer>
        </>
    );
};

export { GoalScreenV2 };